import * as React from 'react'

import { inject, MobXProviderContext, observer } from 'mobx-react'

import MobileResourceEditDrawer from '~/client/src/mobile/components/MobileResource/MobileResourceEditDrawer'
import { useChosenTemplateGate } from '~/client/src/mobile/components/MobileResource/useChosenTemplateGate'
import { useMobileResourceEditRecord } from '~/client/src/mobile/components/MobileResource/useMobileResourceEditRecord'
import { useResetOnOpen } from '~/client/src/mobile/components/MobileResource/useResetOnOpen'
import { FormListSelector } from '~/client/src/shared/components/FormListSelector'
import { FormPropertiesSidebar } from '~/client/src/shared/components/FormPropertiesSidebar'
import { MaterialResourceEditBody } from '~/client/src/shared/components/MaterialResourceEdit/MaterialResourceEditBody'
import { ResourceEdit } from '~/client/src/shared/components/ResourceViewEdit'
import useMaterialResources from '~/client/src/shared/hooks/useMaterialResources'
import { extractControlFields } from '~/client/src/shared/hooks/useResourceControls'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import UserProject from '~/client/src/shared/models/UserProject'
import InitialState from '~/client/src/shared/stores/InitialState'

import {
  useMobileResourceActions,
  useMobileResourceMeta,
} from '../../components/MobileResource/context'

interface IProps {
  state?: InitialState
}

const MobileMaterialResourcesEditPanel: React.FC<IProps> = ({ state }) => {
  const { closeEdit } = useMobileResourceActions()
  const { resourceTypeId, runtimeContext } = useMobileResourceMeta()
  const {
    isOpen,
    activeRecordId,
    selectedRecord,
    editableRecord,
    clearActiveRecord,
  } = useMobileResourceEditRecord()

  const projectId = state?.activeProject?.id
  const { save, schema, controlCommands } = useMaterialResources({
    projectId,
  })

  const stores = React.useContext(MobXProviderContext) as any
  const isAdmin = UserProject.isAdmin(
    stores.state?.user,
    stores.userProjectsStore,
  )

  const [isCreatingNewForm, setIsCreatingNewForm] = React.useState(false)

  const isCreationMode = activeRecordId === 'new'

  const { setChosenTemplateId, effectiveTemplateId, templates } =
    useChosenTemplateGate({
      isCreationMode,
      templates: schema.templates,
      loading: schema.loading,
    })

  useResetOnOpen(isOpen, () => {
    setIsCreatingNewForm(false)
    setChosenTemplateId(null)
  })

  const handleSave = React.useCallback(
    async (formData: Record<string, any>) => {
      await save(activeRecordId, formData)
    },
    [save, activeRecordId],
  )

  if (!activeRecordId) return null

  if (isCreationMode && !effectiveTemplateId) {
    return (
      <MobileResourceEditDrawer
        open={isOpen}
        onClose={closeEdit}
        onCloseComplete={clearActiveRecord}
      >
        {isCreatingNewForm ? (
          <FormPropertiesSidebar
            mode="create"
            resourceTypeId={resourceTypeId}
            onClose={closeEdit}
            onBackToPicker={() => setIsCreatingNewForm(false)}
            resourceSchema={schema}
            onCreated={newTemplateId => {
              setIsCreatingNewForm(false)
              setChosenTemplateId(newTemplateId)
            }}
          />
        ) : (
          <FormListSelector
            title={Localization.translator.selectMaterialForm}
            titleVariant="panel"
            resourceTypeId={resourceTypeId}
            onSelect={setChosenTemplateId}
            onClose={closeEdit}
            onCreateNewForm={
              isAdmin ? () => setIsCreatingNewForm(true) : undefined
            }
            templates={schema.templates}
            loading={schema.loading}
          />
        )}
      </MobileResourceEditDrawer>
    )
  }

  return (
    <MobileResourceEditDrawer
      open={isOpen}
      onClose={closeEdit}
      onCloseComplete={clearActiveRecord}
    >
      <MaterialResourceEditBody
        recordId={activeRecordId}
        record={editableRecord}
        templateId={
          isCreationMode ? effectiveTemplateId || undefined : undefined
        }
        resourceTypeId={resourceTypeId}
        runtimeContext={runtimeContext}
        projectId={projectId}
        isCreationMode={isCreationMode}
        controlFields={extractControlFields(selectedRecord?.root ?? {})}
        controlCommands={controlCommands}
        onClose={closeEdit}
        onSave={handleSave}
        onTemplateChange={setChosenTemplateId}
        resourceSchema={schema}
        formSelector={
          <ResourceEdit.FormSelector
            resourceTypeId={resourceTypeId}
            templates={templates}
            currentTemplateId={effectiveTemplateId}
          />
        }
      />
    </MobileResourceEditDrawer>
  )
}

export default inject('state')(observer(MobileMaterialResourcesEditPanel))
